import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { formatPrice } from '../../utils/price';
import { CartProvider, useCartContext } from '../cart/CartProvider';
import { FormLabel } from '../common/FormLabel';

interface Voucher {
    id: number;
    title: string;
    price: number;
    customAmount: boolean;
}

interface VoucherType {
    name: string;
    vouchers: Voucher[];
}

interface VoucherFormProps {
    locale: string;
    voucherTypes: VoucherType[];
    csrfToken: string;
    cartUrl: string;
}

interface VoucherFormData {
    from: string;
    to: string;
    amount?: string;
}

export const VoucherForm: React.FC<VoucherFormProps> = ({
    locale,
    voucherTypes,
    csrfToken,
    cartUrl,
}) => {
    const { t } = useTranslation();
    const [selectedVoucher, setSelectedVoucher] = useState<Voucher | null>(null);
    const [formData, setFormData] = useState<VoucherFormData>({
        from: '',
        to: '',
        amount: '',
    });
    const [_isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const { addVoucherToCart } = useCartContext();

    const formRef = React.useRef<HTMLDivElement>(null);
    const successMessageRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        i18n.changeLanguage(locale);
    }, [locale]);

    const handleVoucherSelect = (voucher: Voucher) => {
        setSelectedVoucher(voucher);

        // Wait for state update and DOM render
        setTimeout(() => {
            if (formRef.current) {
                formRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }
        }, 100);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const fieldName = e.target.name.match(/\[([^\]]+)\]/)?.[1] || e.target.name;

        if (fieldName === 'amount') {
            // Replace comma with dot for amount field
            const value = e.target.value.replace(',', '.');
            // Ensure it's a valid number
            if (!Number.isNaN(parseFloat(value)) || value === '' || value === '.') {
                setFormData({
                    ...formData,
                    [fieldName]: value,
                });
            }
        } else {
            setFormData({
                ...formData,
                [fieldName]: e.target.value,
            });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedVoucher) return;

        setIsSubmitting(true);
        try {
            await addVoucherToCart({
                purchasableId: selectedVoucher.id,
                qty: 1,
                options: {
                    from: formData.from,
                    to: formData.to,
                    ...(selectedVoucher.customAmount && {
                        amount: formData.amount
                            ? Number(parseFloat(formData.amount).toFixed(2))
                            : 0.0,
                    }),
                },
            });

            // Reset form and show success message
            setFormData({
                from: '',
                to: '',
                amount: '',
            });
            setSelectedVoucher(null);
            setShowSuccess(true);

            // Scroll to success message after it's rendered
            setTimeout(() => {
                if (successMessageRef.current) {
                    successMessageRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
            }, 100);
        } catch (error) {
            console.error('Error adding to cart:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="flex flex-wrap px-4 sm:px-8 py-8 sm:py-16 w-full">
            <h3 className="text-backgroundGreen pb-6 sm:pb-12 text-4xl sm:text-6xl w-full">
                {t('voucher.selectVoucher')}
            </h3>

            {showSuccess ? (
                <div ref={successMessageRef} className="w-full text-center">
                    <p className="text-backgroundGreen text-xl sm:text-2xl mb-6">
                        {t('voucher.successMessage')}
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                        <button
                            type="button"
                            onClick={() => setShowSuccess(false)}
                            className="btn bg-white text-backgroundGreen border border-backgroundGreen hover:bg-beige"
                        >
                            {t('voucher.addAnother')}
                        </button>
                        <a href={cartUrl} className="btn">
                            {t('voucher.viewCart')}
                        </a>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col lg:flex-row w-full">
                    {/* Left side - Voucher List */}
                    <div className="w-full lg:w-2/3 lg:pr-8 mb-8 lg:mb-0">
                        {voucherTypes.map((type) => (
                            <div key={type.name} className="mb-6 sm:mb-8">
                                <h3 className="text-backgroundGreen pb-3 sm:pb-4 text-2xl sm:text-4xl">
                                    {t(`voucher.types.${type.name}`)}
                                </h3>
                                <div className="flex flex-col">
                                    {type.vouchers.map((voucher) => (
                                        <button
                                            key={voucher.id}
                                            type="button"
                                            onClick={() => handleVoucherSelect(voucher)}
                                            className={`flex justify-between p-3 sm:p-4 mb-2 text-left ${
                                                selectedVoucher?.id === voucher.id
                                                    ? 'bg-backgroundGreen text-white'
                                                    : 'bg-white text-orange'
                                            }`}
                                        >
                                            <div>
                                                <div className="text-xl sm:text-3xl mb-1 sm:mb-2">
                                                    {voucher.title}
                                                </div>
                                            </div>
                                            {!voucher.customAmount && (
                                                <div className="font-hikou text-2xl sm:text-4xl">
                                                    {formatPrice(voucher.price, locale)}
                                                </div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Right side - Form */}
                    {selectedVoucher && (
                        <div ref={formRef} className="w-full lg:w-1/3 lg:pt-14">
                            <form
                                method="post"
                                onSubmit={handleSubmit}
                                className="bg-white p-4 sm:p-6"
                            >
                                <input type="hidden" name="CRAFT_CSRF_TOKEN" value={csrfToken} />
                                <input
                                    type="hidden"
                                    name="action"
                                    value="commerce/cart/update-cart"
                                />
                                <input
                                    type="hidden"
                                    name="purchasableId"
                                    value={selectedVoucher.id}
                                />
                                <input type="hidden" name="qty" value="1" />

                                <div className="mb-6 sm:mb-8">
                                    <p className="text-backgroundGreen text-base sm:text-large">
                                        {t('voucher.description_for_customized_vouchers')}
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    {/* From field */}
                                    <div>
                                        <FormLabel
                                            htmlFor="voucher-from"
                                            className="block text-backgroundGreen mb-2 text-sm sm:text-base"
                                        >
                                            {t('voucher.from')}:
                                        </FormLabel>
                                        <input
                                            id="voucher-from"
                                            type="text"
                                            name="options[from]"
                                            value={formData.from}
                                            onChange={handleInputChange}
                                            className="w-full border p-2 text-backgroundGreen font-interstate text-sm sm:text-base"
                                            required
                                        />
                                    </div>

                                    {/* To field */}
                                    <div>
                                        <FormLabel
                                            htmlFor="voucher-to"
                                            className="block text-backgroundGreen mb-2 text-sm sm:text-base"
                                        >
                                            {t('voucher.to')}:
                                        </FormLabel>
                                        <input
                                            id="voucher-to"
                                            type="text"
                                            name="options[to]"
                                            value={formData.to}
                                            onChange={handleInputChange}
                                            className="w-full border p-2 text-backgroundGreen font-interstate text-sm sm:text-base"
                                            required
                                        />
                                    </div>

                                    {/* Amount field (if custom amount) */}
                                    {selectedVoucher.customAmount && (
                                        <div>
                                            <FormLabel
                                                htmlFor="voucher-amount"
                                                className="block text-backgroundGreen mb-2 text-sm sm:text-base"
                                            >
                                                {t('voucher.amount')}:
                                            </FormLabel>
                                            <input
                                                id="voucher-amount"
                                                type="text"
                                                inputMode="decimal"
                                                pattern="[0-9]*[.,]?[0-9]*"
                                                name="options[amount]"
                                                value={formData.amount}
                                                onChange={handleInputChange}
                                                className="w-full border p-2 text-backgroundGreen font-interstate text-sm sm:text-base"
                                                required
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="text-backgroundGreen font-hikou text-2xl sm:text-4xl my-4">
                                    {selectedVoucher.customAmount
                                        ? formData.amount
                                            ? formatPrice(
                                                  parseFloat(formData.amount.replace(',', '.')),
                                                  locale
                                              )
                                            : ''
                                        : formatPrice(selectedVoucher.price, locale)}
                                </div>

                                <button type="submit" className="btn w-full">
                                    {t('voucher.orderVoucher')}
                                </button>
                            </form>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Initialize all voucher form components
const voucherElements = document.querySelectorAll(
    '.voucher-form-component'
) as NodeListOf<HTMLElement>;
voucherElements.forEach((element) => {
    const root = createRoot(element);

    try {
        const locale = element.dataset.locale ?? 'nl';
        const voucherTypes = JSON.parse(element.dataset.vouchers || '[]');
        const csrfToken = element.dataset.csrf || '';
        const cartUrl = element.dataset.cartUrl || '';

        root.render(
            <CartProvider>
                <VoucherForm
                    locale={locale}
                    voucherTypes={voucherTypes}
                    csrfToken={csrfToken}
                    cartUrl={cartUrl}
                />
            </CartProvider>
        );
    } catch (error) {
        console.error('Failed to initialize voucher form:', error);
    }
});
